/**
 * Design System — shared visual tokens and primitives for the app's floating
 * UI (toasts, connection/performance banners, media notifications, tooltips).
 *
 * Before this module, each surface hand-rolled its own colours, fonts, radii
 * and z-index, so notifications drifted apart visually (gradient chip + white
 * text here, solid amber + black text there) and top-center banners stacked on
 * top of each other. Everything floating should now pull from TOKENS and, when
 * pinned to the top-center, share getTopStack() so they queue instead of
 * overlapping.
 *
 * Follows the codebase convention of inline styles and no CSS framework.
 */

import { createElement } from 'lucide';

/**
 * Build an inline Lucide `<svg>` icon node sized and coloured for the UI.
 *
 * All rendered DOM UI icons come from `lucide` via this helper (or `iconSvg`
 * / `setIconButton` below) - no emoji or text glyphs. Inline SVG needs no CSP
 * changes and tree-shakes per-icon through Vite.
 *
 * @param {import('lucide').IconNode} icon - a per-icon export from `lucide`
 *   (e.g. `MessageCircle`)
 * @param {Object} [opts]
 * @param {number} [opts.size] - width/height in px (default 16)
 * @param {number} [opts.strokeWidth] - stroke width (default 2)
 * @param {string} [opts.color] - stroke colour (default 'currentColor')
 * @param {string} [opts.fill] - fill colour (default 'none'); use
 *   'currentColor' for filled glyphs like the level dot
 * @param {string} [opts.label] - when set, the icon is exposed to
 *   assistive tech as `role="img"` + `aria-label` (for icon-only buttons);
 *   otherwise it is `aria-hidden` decoration alongside visible text
 * @returns {SVGSVGElement}
 */
export function createIcon(icon, opts = {}) {
  const { size = 16, strokeWidth = 2, color = 'currentColor', fill = 'none', label } = opts;
  const svg = /** @type {SVGSVGElement} */ (createElement(icon));
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('stroke', color);
  svg.setAttribute('stroke-width', String(strokeWidth));
  svg.setAttribute('fill', fill);
  svg.style.flexShrink = '0';
  svg.style.verticalAlign = '-0.125em';
  if (label) {
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', label);
  } else {
    svg.setAttribute('aria-hidden', 'true');
  }
  return svg;
}

/**
 * Serialise a Lucide icon to an SVG markup string for use inside `innerHTML`
 * templates.
 *
 * ONLY pass a static `lucide` icon export as the first argument - never user
 * data. The output is trusted markup; interpolating it next to unsanitised
 * strings in an `innerHTML` template is safe only because the icon itself is a
 * compile-time constant.
 *
 * @param {import('lucide').IconNode} icon
 * @param {Object} [opts] - same options as {@link createIcon}
 * @returns {string} the icon's `outerHTML`
 */
export function iconSvg(icon, opts = {}) {
  return createIcon(icon, opts).outerHTML;
}

/**
 * Repaint a button as an icon (optionally followed by a text label). The
 * shared primitive for toggle buttons whose icon/label flips with state -
 * clears existing children, applies inline-flex centring with a small gap.
 *
 * @param {HTMLElement} btn - the button to repaint
 * @param {import('lucide').IconNode} icon
 * @param {string} [label] - visible text after the icon; omit for icon-only
 * @param {Object} [opts] - {@link createIcon} options (pass `label` there too
 *   for an accessible name on icon-only buttons)
 * @returns {HTMLElement} the same button
 */
export function setIconButton(btn, icon, label, opts = {}) {
  const iconNode = createIcon(icon, opts);
  if (label) {
    btn.replaceChildren(iconNode, document.createTextNode(label));
  } else {
    btn.replaceChildren(iconNode);
  }
  btn.style.display = 'inline-flex';
  btn.style.alignItems = 'center';
  btn.style.justifyContent = 'center';
  btn.style.gap = '6px';
  return btn;
}

/**
 * Canonical design tokens. Keep this the single source of truth for the
 * floating-UI look; add to it rather than re-introducing literal values.
 */
export const TOKENS = {
  // Body/UI font. The dark configuration panels (GraphicsPanel) intentionally
  // use the 'Sora' display face; notifications use the system stack for fast,
  // legible transient copy.
  font: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",

  // Display face for panel headings and HUD chrome (matches GraphicsPanel /
  // settingsMenu). Body copy inside notifications stays on `font`.
  headingFont: "'Sora', sans-serif",

  // The single in-world accent. Every interactive highlight (active tab,
  // hotspot pulse, coach-mark dot, help headings) uses this so "blue glow"
  // reliably reads as "interactive" everywhere.
  accent: '#8ab4ff',
  accentStrong: '#aecbff',

  // The dark-glass panel language shared by every persistent in-world surface
  // (settings menu, help panel, chat, users panel, hotspot pills, coach marks).
  panel: {
    bg: 'rgba(18, 18, 22, 0.92)',
    bgSoft: 'rgba(18, 18, 22, 0.85)',
    border: '1px solid rgba(255, 255, 255, 0.12)',
    blur: 'blur(8px)',
  },

  focusRing: '0 0 0 2px rgba(138, 180, 255, 0.6)',

  radius: { sm: '6px', md: '8px', lg: '12px', pill: '999px' },

  shadow: {
    sm: '0 2px 10px rgba(0, 0, 0, 0.3)',
    md: '0 4px 20px rgba(0, 0, 0, 0.3)',
  },

  // Vertical rhythm / padding scale.
  space: { xs: '6px', sm: '8px', md: '12px', lg: '16px', xl: '22px' },

  weight: { regular: '400', medium: '500', bold: '700' },

  text: {
    onDark: '#ffffff',
    muted: 'rgba(255, 255, 255, 0.85)',
    subtle: 'rgba(255, 255, 255, 0.6)',
  },

  // Stacking order across the whole app. Higher wins.
  z: {
    worldOverlay: 900, // DOM projected onto the 3D world (hotspot markers)
    overlay: 1000, // in-scene HTML overlays
    hud: 1000, // persistent HUD regions (hudRegions.js)
    notification: 1500, // top-center banners + media notifications (the stack)
    toast: 10000, // bottom transient toasts
    tooltip: 10000,
    modal: 10001, // must sit above everything
  },

  /**
   * Semantic surfaces. Every floating notification uses one of these gradients
   * with white text so the family reads as one system. `level`/`type` strings
   * across the app map onto these keys.
   */
  surface: {
    error: 'linear-gradient(135deg, #dc3545, #c82333)',
    warn: 'linear-gradient(135deg, #f0ad4e, #e08e0b)',
    info: 'linear-gradient(135deg, #17a2b8, #138496)',
    success: 'linear-gradient(135deg, #28a745, #20c997)',
    neutral: 'linear-gradient(135deg, #3a4a5a, #2b3846)',
  },

  /** Action buttons embedded inside notifications. */
  action: {
    primary: { background: '#1976d2', color: '#ffffff' },
    neutral: { background: 'rgba(255, 255, 255, 0.22)', color: '#ffffff' },
  },
};

/**
 * Brand identity constants - the JS source of truth for the marketing-styled
 * surfaces built from modules (arrival screen device check, boot screen,
 * coach-mark celebration). index.html's `:root` CSS variables mirror these
 * for the static shell; keep both in sync when the brand changes.
 */
export const BRAND = {
  primary: '#9900e6',
  secondary: '#ff4dc4',
  accent: '#ff8833',
  gradient: 'linear-gradient(135deg, #9900e6, #ff4dc4, #ff8833)',
  heroGradient: 'linear-gradient(180deg, #ffffff, #fcf5ff)',
  cardGradient: 'linear-gradient(135deg, #fcf5ff, #fff5fc)',
  shadow: '0 10px 40px -10px rgba(153, 0, 230, 0.3)',
  font: "'Sora', sans-serif",
};

/**
 * Resolve a semantic surface gradient, defaulting to `info`.
 * @param {string} [level] - one of TOKENS.surface keys
 * @returns {string} CSS background value
 */
export function surfaceFor(level) {
  return TOKENS.surface[level] || TOKENS.surface.info;
}

/**
 * The shared top-center vertical stack. Any notification pinned to the top of
 * the viewport (connection/performance banner, media-retry prompt) should be
 * appended here instead of being individually `position: fixed`, so they queue
 * vertically with a consistent gap and can never overlap.
 *
 * The container itself is pass-through (pointerEvents: none); interactive
 * children opt back in with `pointerEvents: auto`.
 *
 * @returns {HTMLElement} the stack container
 */
export function getTopStack() {
  let stack = document.getElementById('oggi-top-stack');
  if (stack) return stack;

  stack = document.createElement('div');
  stack.id = 'oggi-top-stack';
  Object.assign(stack.style, {
    position: 'fixed',
    top: TOKENS.space.md,
    left: '50%',
    transform: 'translateX(-50%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: TOKENS.space.sm,
    zIndex: String(TOKENS.z.notification),
    pointerEvents: 'none',
    maxWidth: 'calc(100vw - 24px)',
  });
  document.body.appendChild(stack);
  return stack;
}

/**
 * Apply the shared notification-card look to an element (gradient surface,
 * white text, radius, shadow, system font). Positioning is left to the caller
 * (the top stack owns layout for stacked banners).
 * @param {HTMLElement} el
 * @param {Object} [options]
 * @param {string} [options.level] - semantic surface key (default 'info')
 * @param {string} [options.fontSize] - default '15px'
 */
export function applyCardStyle(el, options = {}) {
  const { level = 'info', fontSize = '15px' } = options;
  Object.assign(el.style, {
    background: surfaceFor(level),
    color: TOKENS.text.onDark,
    padding: `${TOKENS.space.sm} ${TOKENS.space.xl}`,
    borderRadius: TOKENS.radius.md,
    fontFamily: TOKENS.font,
    fontSize,
    fontWeight: TOKENS.weight.medium,
    boxShadow: TOKENS.shadow.md,
  });
}

/**
 * Create an action button styled for the inside of a notification card.
 * @param {string} text
 * @param {Object} [options]
 * @param {'primary'|'neutral'} [options.variant] - default 'primary'
 * @returns {HTMLButtonElement}
 */
export function createActionButton(text, options = {}) {
  const { variant = 'primary' } = options;
  const palette = TOKENS.action[variant] || TOKENS.action.primary;
  const button = document.createElement('button');
  button.textContent = text;
  Object.assign(button.style, {
    background: palette.background,
    color: palette.color,
    border: 'none',
    borderRadius: TOKENS.radius.sm,
    padding: '6px 14px',
    fontFamily: TOKENS.font,
    fontSize: '13px',
    fontWeight: TOKENS.weight.medium,
    cursor: 'pointer',
  });
  return button;
}

/**
 * Apply the shared dark-glass panel look (settings menu / help panel / chat /
 * hotspot pill family) to an element. Positioning and layout stay with the
 * caller.
 * @param {HTMLElement} el
 * @param {Object} [options]
 * @param {boolean} [options.soft] - use the lighter bg for transient surfaces
 * @param {string} [options.radius] - default TOKENS.radius.lg
 */
export function applyPanelStyle(el, options = {}) {
  const { soft = false, radius = TOKENS.radius.lg } = options;
  Object.assign(el.style, {
    background: soft ? TOKENS.panel.bgSoft : TOKENS.panel.bg,
    color: TOKENS.text.onDark,
    border: TOKENS.panel.border,
    borderRadius: radius,
    backdropFilter: TOKENS.panel.blur,
    boxShadow: TOKENS.shadow.md,
    fontFamily: TOKENS.headingFont,
  });
}

/**
 * Create a persistent HUD button (the Help / Menu family): dark glass pill
 * with a Lucide icon and a visible text label, sized for touch (min 36px tall,
 * comfortably tappable with padding).
 * @param {import('lucide').IconNode} icon
 * @param {string} label - visible text (also the accessible name)
 * @param {Object} [options]
 * @param {string} [options.title] - hover tooltip; defaults to label
 * @returns {HTMLButtonElement}
 */
export function createHudButton(icon, label, options = {}) {
  const { title = label } = options;
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.title = title;
  Object.assign(btn.style, {
    background: 'rgba(0, 0, 0, 0.6)',
    color: TOKENS.text.onDark,
    border: '1px solid rgba(255, 255, 255, 0.2)',
    borderRadius: TOKENS.radius.md,
    padding: '8px 14px',
    minHeight: '36px',
    fontFamily: TOKENS.headingFont,
    fontSize: '13px',
    cursor: 'pointer',
    pointerEvents: 'auto',
    backdropFilter: 'blur(4px)',
  });
  setIconButton(btn, icon, label, { size: 14 });
  return btn;
}

/**
 * Create the accent primary CTA used inside dark panels (welcome card,
 * settings actions).
 * @param {string} text
 * @returns {HTMLButtonElement}
 */
export function createPrimaryButton(text) {
  const btn = document.createElement('button');
  btn.type = 'button';
  btn.textContent = text;
  Object.assign(btn.style, {
    background: TOKENS.accent,
    color: '#10131a',
    border: 'none',
    borderRadius: TOKENS.radius.md,
    padding: '10px 18px',
    fontFamily: TOKENS.headingFont,
    fontSize: '14px',
    fontWeight: TOKENS.weight.bold,
    cursor: 'pointer',
  });
  btn.addEventListener('mouseenter', () => {
    btn.style.background = TOKENS.accentStrong;
  });
  btn.addEventListener('mouseleave', () => {
    btn.style.background = TOKENS.accent;
  });
  return btn;
}
